import apiConfig from "../config/api.js";

/**
 * Service pour gérer le partage de recettes avec optimisations
 */

class ShareService {
  constructor() {
    this.baseURL = apiConfig.baseURL;
    this.cache = new Map();
    this.lastFetch = 0;
    this.cacheTimeout = 5000; // 5 secondes de cache
  }

  /**
   * Récupérer les recettes partagées avec cache intelligent
   */
  async getSharedRecipes(forceRefresh = false) {
    const now = Date.now();

    // Utiliser le cache si disponible et pas trop ancien
    if (
      !forceRefresh &&
      this.cache.has("shared-recipes") &&
      now - this.lastFetch < this.cacheTimeout
    ) {
      console.log("📦 Utilisation du cache pour les recettes partagées");
      return this.cache.get("shared-recipes");
    }

    try {
      console.log("🌐 Récupération des recettes partagées depuis l'API...");

      const response = await fetch(`${this.baseURL}/recipes/shared/public`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Mettre en cache
      this.cache.set("shared-recipes", data);
      this.lastFetch = now;

      console.log(`✅ ${data.data?.length || 0} recettes partagées récupérées`);
      return data;
    } catch (error) {
      console.error(
        "❌ Erreur lors de la récupération des recettes partagées:",
        error
      );

      // Retourner le cache en cas d'erreur
      if (this.cache.has("shared-recipes")) {
        console.log("📦 Retour du cache en cas d'erreur");
        return this.cache.get("shared-recipes");
      }

      throw error;
    }
  }

  /**
   * Partager une recette
   */
  async shareRecipe(recipeId, shareOptions, token) {
    try {
      console.log(`📤 Partage de la recette ${recipeId}...`);

      const response = await fetch(
        `${this.baseURL}/recipes/${recipeId}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(shareOptions),
          signal: AbortSignal.timeout(15000),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Erreur ${response.status}`);
      }

      // Invalider le cache après un partage
      this.cache.delete("shared-recipes");

      console.log("✅ Recette partagée avec succès");
      return data;
    } catch (error) {
      console.error("❌ Erreur lors du partage:", error);
      throw error;
    }
  }

  /**
   * Vérifier la connectivité
   */
  async checkConnectivity() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: "GET",
        signal: AbortSignal.timeout(3000),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Nettoyer le cache
   */
  clearCache() {
    this.cache.clear();
    this.lastFetch = 0;
    console.log("🧹 Cache nettoyé");
  }
}

// Instance singleton
const shareService = new ShareService();
export default shareService;
